import React, { useState, useEffect } from 'react';
import { Plus, Search, FileText, AlertCircle, CheckCircle2, Clock, X, Image, Trash2, ClipboardCheck, Filter, ChevronLeft, ChevronRight, Calendar, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import * as XLSX from 'xlsx';
import clsx from 'clsx';

interface Hallazgo {
  id: string;
  maquinaria_id: string;
  descripcion: string;
  fecha: string;
  created_at: string;
  created_by: string;
  maquinarias: {
    nombre: string;
    codigo: string;
    tipo: string;
    patente: string | null;
  };
}

interface Checklist {
  id: string;
  maquinaria_id: string;
  fecha: string;
  created_at: string;
  operator_id: string;
  maquinarias: {
    nombre: string;
    codigo: string;
    tipo: string;
    patente: string | null;
  };
  operators: {
    name: string;
    rut: string;
  };
  checklist_items: {
    id: string;
    nombre: string;
    estado: 'bueno' | 'regular' | 'malo';
    observaciones: string;
  }[];
}

interface PhotosModalProps {
  hallazgoId: string;
  isOpen: boolean;
  onClose: () => void;
}

const PhotosModal = ({ hallazgoId, isOpen, onClose }: PhotosModalProps) => {
  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPhotos = async () => {
      if (!isOpen || !hallazgoId) return;

      try {
        setLoading(true);
        setError(null);

        const { data: hallazgo, error: hallazgoError } = await supabase
          .from('hallazgos')
          .select('maquinaria_id, created_at')
          .eq('id', hallazgoId)
          .single();

        if (hallazgoError) throw hallazgoError;
        if (!hallazgo) throw new Error('Hallazgo not found');

        const createdAt = new Date(hallazgo.created_at);
        const folderPath = `${hallazgo.maquinaria_id}/${createdAt.toISOString().replace(/[:.]/g, '-')}`;

        const { data: files, error: listError } = await supabase
          .storage
          .from('hallazgos-fotos')
          .list(folderPath);

        if (listError) throw listError;

        if (!files || files.length === 0) {
          setPhotos([]);
          return;
        }

        const photoUrls = files
          .filter(file => /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name))
          .map(file => {
            const { data } = supabase
              .storage
              .from('hallazgos-fotos')
              .getPublicUrl(`${folderPath}/${file.name}`);
            return data.publicUrl;
          });

        setPhotos(photoUrls);
      } catch (err) {
        console.error('Error fetching photos:', err);
        setError('Error al cargar las fotos');
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, [hallazgoId, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Fotos del Hallazgo</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004A98]"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
              <p className="mt-2 text-sm text-red-600">{error}</p>
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-8">
              <Image className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No hay fotos adjuntas a este hallazgo</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {photos.map((url, index) => (
                <div key={index} className="relative group">
                  <img
                    src={url}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-48 object-cover rounded-lg"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://via.placeholder.com/400x300?text=Error+al+cargar+imagen';
                    }}
                  />
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
                  >
                    <span className="text-white bg-black bg-opacity-75 px-3 py-1 rounded-full text-sm">
                      Ver imagen
                    </span>
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ChecklistModal = ({ checklist, onClose }: { checklist: Checklist; onClose: () => void }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Detalles del Checklist</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
          <X className="h-6 w-6" />
        </button>
      </div>
      <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Información General</h4>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Maquinaria</dt>
                <dd className="mt-1 text-sm text-gray-900">{checklist.maquinarias.nombre}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Código</dt>
                <dd className="mt-1 text-sm text-gray-900">{checklist.maquinarias.codigo}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Operador</dt>
                <dd className="mt-1 text-sm text-gray-900">{checklist.operators.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">RUT</dt>
                <dd className="mt-1 text-sm text-gray-900">{checklist.operators.rut}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Fecha</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(checklist.fecha).toLocaleString('es-CL')}
                </dd>
              </div>
            </dl>
          </div>

          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Items del Checklist</h4>
            <div className="space-y-4">
              {checklist.checklist_items.map((item) => (
                <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{item.nombre}</span>
                    <span className={clsx(
                      'px-2 py-1 rounded-full text-xs font-medium',
                      item.estado === 'bueno' && 'bg-green-100 text-green-800',
                      item.estado === 'regular' && 'bg-yellow-100 text-yellow-800',
                      item.estado === 'malo' && 'bg-red-100 text-red-800'
                    )}>
                      {item.estado.charAt(0).toUpperCase() + item.estado.slice(1)}
                    </span>
                  </div>
                  {item.observaciones && (
                    <p className="mt-2 text-sm text-gray-500">{item.observaciones}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Componente de Paginación reutilizable
const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalItems,
  itemsPerPage,
  onPageChange 
}: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  if (totalPages <= 1) return null;

  return (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
            currentPage === 1
              ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          }`}
        >
          Anterior
        </button>
        <span className="text-sm text-gray-700 flex items-center">
          Página {currentPage} de {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
            currentPage === totalPages
              ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          }`}
        >
          Siguiente
        </button>
      </div>
      
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Mostrando <span className="font-medium">{startIndex}</span> a{' '}
            <span className="font-medium">{endIndex}</span> de{' '}
            <span className="font-medium">{totalItems}</span> resultados
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                currentPage === 1
                  ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 2 && page <= currentPage + 2)
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-[#004A98] border-[#004A98] text-white'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              }
              
              if (page === currentPage - 3 || page === currentPage + 3) {
                return (
                  <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    ...
                  </span>
                );
              }
              
              return null;
            })}
            
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                currentPage === totalPages
                  ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export const ReportesList = () => {
  const [activeTab, setActiveTab] = useState<'hallazgos' | 'checklists'>('hallazgos');
  const [hallazgos, setHallazgos] = useState<Hallazgo[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMaquinaria, setSelectedMaquinaria] = useState<string>('');
  const [maquinarias, setMaquinarias] = useState<Array<{ id: string; nombre: string }>>([]);
  const [selectedHallazgo, setSelectedHallazgo] = useState<string | null>(null);
  const [showPhotosModal, setShowPhotosModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(null);
  
  // Estados de paginación
  const [hallazgosPage, setHallazgosPage] = useState(1);
  const [checklistsPage, setChecklistsPage] = useState(1);
  const itemsPerPage = 10; // Puedes ajustar este valor

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: maquinariasData, error: maquinariasError } = await supabase
          .from('maquinarias')
          .select('id, nombre')
          .order('nombre');

        if (maquinariasError) throw maquinariasError;
        setMaquinarias(maquinariasData || []);

        const { data: hallazgosData, error: hallazgosError } = await supabase
          .from('hallazgos')
          .select(`
            *,
            maquinarias (
              nombre,
              codigo,
              tipo,
              patente
            )
          `)
          .order('fecha', { ascending: false });

        if (hallazgosError) throw hallazgosError;
        setHallazgos(hallazgosData || []);

        const { data: checklistsData, error: checklistsError } = await supabase
          .from('checklists')
          .select(`
            *,
            maquinarias (
              nombre,
              codigo,
              tipo,
              patente
            ),
            operators (
              name,
              rut
            ),
            checklist_items (
              id,
              nombre,
              estado,
              observaciones
            )
          `)
          .order('fecha', { ascending: false });

        if (checklistsError) throw checklistsError;
        setChecklists(checklistsData || []);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteHallazgo = async () => {
    if (!selectedHallazgo) return;

    try {
      setIsDeleting(true);

      const { data: hallazgo, error: hallazgoError } = await supabase
        .from('hallazgos')
        .select('maquinaria_id, created_at')
        .eq('id', selectedHallazgo)
        .single();

      if (hallazgoError) throw hallazgoError;

      const createdAt = new Date(hallazgo.created_at);
      const folderPath = `${hallazgo.maquinaria_id}/${createdAt.toISOString().replace(/[:.]/g, '-')}`;

      const { data: files } = await supabase
        .storage
        .from('hallazgos-fotos')
        .list(folderPath);

      if (files && files.length > 0) {
        const filesToDelete = files.map(file => `${folderPath}/${file.name}`);
        await supabase
          .storage
          .from('hallazgos-fotos')
          .remove(filesToDelete);
      }

      const { error: deleteError } = await supabase
        .from('hallazgos')
        .delete()
        .eq('id', selectedHallazgo);

      if (deleteError) throw deleteError;

      setHallazgos(prev => prev.filter(h => h.id !== selectedHallazgo));
      setShowDeleteModal(false);
      setSelectedHallazgo(null);

    } catch (err) {
      console.error('Error deleting hallazgo:', err);
      setError(err instanceof Error ? err.message : 'Error al eliminar el hallazgo');
    } finally {
      setIsDeleting(false);
    }
  };

  // Filtrado de datos
  const filteredHallazgos = hallazgos.filter(hallazgo =>
    hallazgo.maquinarias.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    hallazgo.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
    hallazgo.created_by.toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(hallazgo => 
    selectedMaquinaria ? hallazgo.maquinaria_id === selectedMaquinaria : true
  );

  const filteredChecklists = checklists.filter(checklist =>
    checklist.maquinarias.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    checklist.operators.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    checklist.operators.rut.toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(checklist => 
    selectedMaquinaria ? checklist.maquinaria_id === selectedMaquinaria : true
  );

  // Cálculos de paginación
  const totalHallazgosPages = Math.ceil(filteredHallazgos.length / itemsPerPage);
  const totalChecklistsPages = Math.ceil(filteredChecklists.length / itemsPerPage);
  
  const paginatedHallazgos = filteredHallazgos.slice(
    (hallazgosPage - 1) * itemsPerPage,
    hallazgosPage * itemsPerPage
  );
  
  const paginatedChecklists = filteredChecklists.slice(
    (checklistsPage - 1) * itemsPerPage,
    checklistsPage * itemsPerPage
  );

  // Reset página cuando cambian los filtros
  useEffect(() => {
    setHallazgosPage(1);
    setChecklistsPage(1);
  }, [searchTerm, selectedMaquinaria]);

  const handleExportExcel = () => {
    if (activeTab === 'hallazgos') {
      const dataToExport = filteredHallazgos.map(hallazgo => ({
        'Fecha': new Date(hallazgo.fecha).toLocaleString('es-CL'),
        'Maquinaria': hallazgo.maquinarias.nombre,
        'Código': hallazgo.maquinarias.codigo,
        'Tipo': hallazgo.maquinarias.tipo,
        'Patente': hallazgo.maquinarias.patente || 'N/A',
        'Descripción': hallazgo.descripcion,
        'Reportado por': hallazgo.created_by,
        'Fecha de registro': new Date(hallazgo.created_at).toLocaleString('es-CL')
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Hallazgos');
      XLSX.writeFile(wb, 'hallazgos.xlsx');
    } else {
      const dataToExport = filteredChecklists.map(checklist => ({
        'Fecha': new Date(checklist.fecha).toLocaleString('es-CL'),
        'Maquinaria': checklist.maquinarias.nombre,
        'Código': checklist.maquinarias.codigo,
        'Tipo': checklist.maquinarias.tipo,
        'Patente': checklist.maquinarias.patente || 'N/A',
        'Operador': checklist.operators.name,
        'RUT Operador': checklist.operators.rut,
        'Items Buenos': checklist.checklist_items.filter(item => item.estado === 'bueno').length,
        'Items Regulares': checklist.checklist_items.filter(item => item.estado === 'regular').length,
        'Items Malos': checklist.checklist_items.filter(item => item.estado === 'malo').length,
        'Fecha de registro': new Date(checklist.created_at).toLocaleString('es-CL')
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Checklists');
      XLSX.writeFile(wb, 'checklists.xlsx');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004A98]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con tabs y controles */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reportes</h1>
            <p className="mt-1 text-sm text-gray-500">
              Gestión de hallazgos y checklists
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex border border-gray-300 rounded-md p-1">
              <button
                onClick={() => {
                  setActiveTab('hallazgos');
                  setHallazgosPage(1);
                }}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                  activeTab === 'hallazgos'
                    ? 'bg-[#004A98] text-white'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                Hallazgos ({filteredHallazgos.length})
              </button>
              <button
                onClick={() => {
                  setActiveTab('checklists');
                  setChecklistsPage(1);
                }}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                  activeTab === 'checklists'
                    ? 'bg-[#004A98] text-white'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                Checklists ({filteredChecklists.length})
              </button>
            </div>
            <button
              onClick={handleExportExcel}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#004A98] hover:bg-[#003875] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#004A98]"
            >
              <FileText className="h-4 w-4 mr-2" />
              Exportar
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={activeTab === 'hallazgos' 
                ? "Buscar por maquinaria, descripción o usuario..."
                : "Buscar por maquinaria u operador..."}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-[#004A98] focus:border-[#004A98] sm:text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="sm:w-64">
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-md">
              <Filter className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <select
                value={selectedMaquinaria}
                onChange={(e) => setSelectedMaquinaria(e.target.value)}
                className="block w-full bg-transparent border-none shadow-none focus:ring-0 sm:text-sm"
              >
                <option value="">Todas las Maquinarias</option>
                {maquinarias.map((maquinaria) => (
                  <option key={maquinaria.id} value={maquinaria.id}>
                    {maquinaria.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de Hallazgos */}
      {activeTab === 'hallazgos' ? (
        <>
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            {paginatedHallazgos.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay hallazgos</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No se encontraron hallazgos con los filtros aplicados.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Fecha
                          </div>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Maquinaria
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Descripción
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            Reportado por
                          </div>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedHallazgos.map((hallazgo) => (
                        <tr key={hallazgo.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 text-gray-400 mr-2" />
                              {new Date(hallazgo.fecha).toLocaleString('es-CL', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {hallazgo.maquinarias.nombre}
                            </div>
                            <div className="text-sm text-gray-500">
                              {hallazgo.maquinarias.codigo} • {hallazgo.maquinarias.tipo}
                            </div>
                            {hallazgo.maquinarias.patente && (
                              <div className="text-xs text-gray-500">
                                Patente: {hallazgo.maquinarias.patente}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <div className="max-w-lg break-words">
                              {hallazgo.descripcion}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {hallazgo.created_by}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => {
                                  setSelectedHallazgo(hallazgo.id);
                                  setShowPhotosModal(true);
                                }}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-[#004A98] bg-[#004A98]/10 hover:bg-[#004A98]/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#004A98]"
                              >
                                <Image className="h-4 w-4 mr-2" />
                                Ver fotos
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedHallazgo(hallazgo.id);
                                  setShowDeleteModal(true);
                                }}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-red-600 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Eliminar
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={hallazgosPage}
                  totalPages={totalHallazgosPages}
                  totalItems={filteredHallazgos.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setHallazgosPage}
                />
              </>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Tabla de Checklists */}
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            {paginatedChecklists.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardCheck className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay checklists</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No se encontraron checklists con los filtros aplicados.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Fecha
                          </div>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Maquinaria
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            Operador
                          </div>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado Items
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedChecklists.map((checklist) => {
                        const itemsCount = {
                          bueno: checklist.checklist_items.filter(item => item.estado === 'bueno').length,
                          regular: checklist.checklist_items.filter(item => item.estado === 'regular').length,
                          malo: checklist.checklist_items.filter(item => item.estado === 'malo').length
                        };

                        return (
                          <tr key={checklist.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                                {new Date(checklist.fecha).toLocaleString('es-CL', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {checklist.maquinarias.nombre}
                              </div>
                              <div className="text-sm text-gray-500">
                                {checklist.maquinarias.codigo} • {checklist.maquinarias.tipo}
                              </div>
                              {checklist.maquinarias.patente && (
                                <div className="text-xs text-gray-500">
                                  Patente: {checklist.maquinarias.patente}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {checklist.operators.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {checklist.operators.rut}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                  <span className="text-sm text-gray-600">{itemsCount.bueno} buenos</span>
                                </div>
                                {itemsCount.regular > 0 && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                    <span className="text-sm text-gray-600">{itemsCount.regular} regulares</span>
                                  </div>
                                )}
                                {itemsCount.malo > 0 && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                    <span className="text-sm text-gray-600">{itemsCount.malo} malos</span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => {
                                  setSelectedChecklist(checklist);
                                  setShowChecklistModal(true);
                                }}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-[#004A98] bg-[#004A98]/10 hover:bg-[#004A98]/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#004A98]"
                              >
                                <ClipboardCheck className="h-4 w-4 mr-2" />
                                Ver detalles
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={checklistsPage}
                  totalPages={totalChecklistsPages}
                  totalItems={filteredChecklists.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setChecklistsPage}
                />
              </>
            )}
          </div>
        </>
      )}

      {/* Modales */}
      {selectedChecklist && showChecklistModal && (
        <ChecklistModal
          checklist={selectedChecklist}
          onClose={() => {
            setSelectedChecklist(null);
            setShowChecklistModal(false);
          }}
        />
      )}

      {selectedHallazgo && showPhotosModal && (
        <PhotosModal
          hallazgoId={selectedHallazgo}
          isOpen={showPhotosModal}
          onClose={() => {
            setSelectedHallazgo(null);
            setShowPhotosModal(false);
          }}
        />
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmar eliminación</h3>
            <p className="text-sm text-gray-500 mb-4">
              ¿Estás seguro de que deseas eliminar este hallazgo? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedHallazgo(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteHallazgo}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};